import * as React from "react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * A lightweight exact-date range picker for the "which timeframe" clarification.
 * Built on the existing Popover + native date inputs (no calendar dependency).
 * On apply it emits the same encoded {@code start/end} value the preset chips use,
 * so the backend decodes it identically.
 */
export function DateRangePicker({
  disabled,
  onPick,
}: {
  disabled?: boolean;
  onPick: (label: string, value: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [start, setStart] = React.useState("");
  const [end, setEnd] = React.useState("");
  const valid = start !== "" && end !== "" && start <= end;

  function apply() {
    if (!valid) {
      return;
    }
    onPick(`${start} to ${end}`, `${start}/${end}`);
    setOpen(false);
    setStart("");
    setEnd("");
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="rounded-full border border-brand-blue/40 bg-brand-blue/5 px-3 py-1 text-xs font-medium text-brand-blue transition hover:bg-brand-blue hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          Pick exact dates…
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-2">
        <div className="space-y-1">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">From</span>
          <input
            type="date"
            value={start}
            max={end || undefined}
            onChange={(e) => setStart(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm focus-visible:border-brand-blue/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/30"
          />
        </div>
        <div className="space-y-1">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">To</span>
          <input
            type="date"
            value={end}
            min={start || undefined}
            onChange={(e) => setEnd(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm focus-visible:border-brand-blue/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/30"
          />
        </div>
        <button
          type="button"
          disabled={!valid}
          onClick={apply}
          className="w-full rounded-md bg-brand-blue px-2 py-1 text-xs font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Use these dates
        </button>
      </PopoverContent>
    </Popover>
  );
}
